import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";

const API = process.env.REACT_APP_BACKEND_URL;

const UNIT_TYPES = ["Condo / Apartment", "Townhome", "Detached house", "Multiple units", "Not sure yet"];

const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

export const RentAnalysis = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", unit_type: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.name.trim()) err.name = "Your name, please.";
    if (!emailOk(form.email)) err.email = "A valid email, please.";
    if (!form.address.trim()) err.address = "Where is the property?";
    setErrors(err);
    if (Object.keys(err).length) return;

    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/owner-inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("bad status");
      setStatus("done");
    } catch (_) {
      setStatus("error");
    }
  };

  const field = "mt-2 h-12 rounded-xl border-[rgba(32,30,26,0.18)] bg-white text-[15px] focus-visible:ring-larch";

  return (
    <section id="analysis" className="grain relative" style={{ backgroundColor: "#24382E" }} data-nav-theme="dark" data-testid="analysis-section">
      <div className="relative z-10 mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-6 py-24 md:grid-cols-12 md:gap-10 md:px-10 md:py-36">
        {/* Left copy */}
        <div className="md:col-span-5 md:pr-8">
          <Reveal><Eyebrow color="#AEC4C7">Free rent analysis</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display mt-7" style={{ fontSize: "clamp(36px,5vw,64px)", color: "#F8F5EF", lineHeight: 1.02 }}>
              What could your place <span className="italic" style={{ color: "#E3B268" }}>actually earn?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="body-measure mt-6 text-[17px]" style={{ color: "rgba(237,233,224,0.78)" }}>
              Tell us the address and we'll send a real number — comparable rents on your
              block, days-to-lease, and what we'd do first. No obligation, usually same day.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="label-caps mt-8 text-[11px]" style={{ color: "rgba(174,196,199,0.8)" }}>
              A real person reads every one — no bots.
            </p>
          </Reveal>
        </div>

        {/* Form card */}
        <div className="md:col-span-7">
          <div className="p-8 md:p-10" style={{ backgroundColor: "#F8F5EF", borderRadius: 24, boxShadow: "0 40px 80px -40px rgba(0,0,0,0.5)" }}>
            <AnimatePresence mode="wait">
              {status === "done" ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-start py-10"
                  data-testid="analysis-success"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: "#C89A4E" }}>
                    <Check size={28} color="#14231B" />
                  </span>
                  <h3 className="font-display mt-6" style={{ fontSize: "clamp(28px,3vw,40px)", color: "#24382E" }}>
                    Got it — we're on it.
                  </h3>
                  <p className="mt-3 text-[16px]" style={{ color: "rgba(32,30,26,0.72)" }}>
                    Your analysis is being put together by hand. Expect a real number from a
                    real person — usually the same day, always within one.
                  </p>
                </motion.div>
              ) : (
                <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={submit} data-testid="analysis-form" noValidate>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <Label className="label-caps text-[11px]" style={{ color: "#24382E" }}>Your name</Label>
                      <Input className={field} value={form.name} onChange={set("name")} placeholder="Jordan Reeve" data-testid="input-name" />
                      {errors.name && <p className="mt-1 text-[12px]" style={{ color: "#8a3b2f" }}>{errors.name}</p>}
                    </div>
                    <div>
                      <Label className="label-caps text-[11px]" style={{ color: "#24382E" }}>Email</Label>
                      <Input className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="input-email" />
                      {errors.email && <p className="mt-1 text-[12px]" style={{ color: "#8a3b2f" }}>{errors.email}</p>}
                    </div>
                    <div>
                      <Label className="label-caps text-[11px]" style={{ color: "#24382E" }}>Phone (optional)</Label>
                      <Input className={field} value={form.phone} onChange={set("phone")} placeholder="403 555 0199" data-testid="input-phone" />
                    </div>
                    <div>
                      <Label className="label-caps text-[11px]" style={{ color: "#24382E" }}>Unit type</Label>
                      <Select value={form.unit_type} onValueChange={set("unit_type")}>
                        <SelectTrigger className={field} data-testid="select-unit-type">
                          <SelectValue placeholder="Select one" />
                        </SelectTrigger>
                        <SelectContent>
                          {UNIT_TYPES.map((u) => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="label-caps text-[11px]" style={{ color: "#24382E" }}>Property address</Label>
                      <Input className={field} value={form.address} onChange={set("address")} placeholder="123 4th Street SW, Mission" data-testid="input-address" />
                      {errors.address && <p className="mt-1 text-[12px]" style={{ color: "#8a3b2f" }}>{errors.address}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="label-caps text-[11px]" style={{ color: "#24382E" }}>Anything else? (optional)</Label>
                      <Textarea className="mt-2 min-h-[96px] rounded-xl border-[rgba(32,30,26,0.18)] bg-white text-[15px] focus-visible:ring-larch" value={form.message} onChange={set("message")} placeholder="Vacancy date, current rent, quirks we should know…" data-testid="input-message" />
                    </div>
                  </div>

                  {status === "error" && (
                    <p className="mt-4 text-[14px]" style={{ color: "#8a3b2f" }} data-testid="analysis-error">
                      Something snagged on our end — please try again, or email hello@roamliving.ca.
                    </p>
                  )}

                  <button type="submit" disabled={status === "sending"} className="pill pill-pine mt-7 w-full text-base disabled:opacity-70" data-testid="submit-analysis">
                    {status === "sending" ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : "Get my free rent analysis"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RentAnalysis;
